// src/pages/CampaignFlow/phases/Phase2Creation.jsx
import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  PaintBrushIcon,
  PhotoIcon,
  PencilSquareIcon,
  UserGroupIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import CreativeCanvasEngine from '../../../engine/CreativeCanvasEngine/CreativeCanvasEngine';
import apiClient from '../../../lib/apiClient';

export default function Phase2Creation({ campaignId, campaignName, objective, onNext }) {
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [structure, setStructure]     = useState(null);
  const [headline, setHeadline]       = useState('');
  const [audience, setAudience]       = useState(null);
  const [editingText, setEditingText] = useState(false);
  const [imageQuery, setImageQuery]   = useState('');
  const [imgLoading, setImgLoading]   = useState(false);
  const [toast, setToast]             = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiClient.request('/creative/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: `${campaignName} — ${objective}`,
          format: 'design',
          business_type: campaignName,
          creative_type:  'design',
        }),
      });
      const d = await r.json();
      if (d.success || d.structure) {
        const st = d.structure || d.creative;
        setStructure(st);
        // Extrai headline da primeira layer de texto
        const layers = st?.canvas_data?.layers || [];
        const textLayer = layers.find(l => l.type === 'text' || l.type === 'textbox');
        if (textLayer?.text) setHeadline(textLayer.text);

        // Gera sugestão de público via IA
        const pubRes = await apiClient.request('/mp/generate', {
          method: 'POST',
          body: JSON.stringify({ nicho: campaignName, diferencial: objective, dor_cliente: objective, tom: 'profissional' }),
        });
        const pubD = await pubRes.json();
        const pub = pubD?.copy?.publico_sugerido || pubD?.audience_suggestion || null;
        setAudience(pub);

        if (pub) {
          // Persiste audience junto ao campaign
          await apiClient.request(`/campaign/${campaignId}`, {
            method: 'PUT',
            body: JSON.stringify({ audience_suggestion: pub, creative_id: d.creative_id || null }),
          });
        }
        showToast('Criativo gerado pela IA!');
      } else {
        setError(d.message || 'Falha ao gerar criativo');
      }
    } catch {
      setError('Erro de conexão ao gerar criativo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); }, []); // eslint-disable-line

  // Mudar imagem via busca Pexels
  const handleImageSearch = async () => {
    const q = imageQuery.trim() || campaignName;
    setImgLoading(true);
    try {
      const r = await apiClient.request('/mp/image/search', {
        method: 'POST',
        body: JSON.stringify({ keyword: q }),
      });
      const d = await r.json();
      if (d.image_url && structure) {
        const newStructure = JSON.parse(JSON.stringify(structure));
        const layers = newStructure?.canvas_data?.layers || newStructure?.slides?.[0]?.canvas_data?.layers || [];
        const imgLayer = layers.find(l => l.type === 'image');
        if (imgLayer) {
          imgLayer.src = d.image_url;
          setStructure({ ...newStructure, _v: Date.now() });
          showToast('Imagem atualizada!');
        } else {
          showToast('Imagem obtida mas não há layer de imagem no criativo');
        }
      } else {
        showToast('Nenhuma imagem encontrada para essa busca');
      }
    } catch {
      showToast('Erro ao buscar imagem');
    } finally {
      setImgLoading(false);
    }
  };

  // Atualizar headline na layer de texto
  const handleUpdateHeadline = () => {
    if (!headline.trim() || !structure) return;
    const newStructure = JSON.parse(JSON.stringify(structure));
    const layers = newStructure?.canvas_data?.layers || newStructure?.slides?.[0]?.canvas_data?.layers || [];
    const textLayer = layers.find(l => l.type === 'text' || l.type === 'textbox');
    if (textLayer) {
      textLayer.text = headline;
      setStructure({ ...newStructure, _v: Date.now() });
      showToast('Texto atualizado!');
    }
    setEditingText(false);
    // Persiste headline
    apiClient.request(`/campaign/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify({ headline }),
    });
  };

  const handleNext = async () => {
    await apiClient.request(`/campaign/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify({ step: 'boost' }),
    });
    onNext({ structure, headline, audience });
  };

  if (loading) {
    return (
      <div className="cf-loading">
        <CpuChipIcon className="cf-loading__mascot" style={{ width: 48, height: 48 }} />
        <div className="cf-loading__dots"><span/><span/><span/></div>
        <p>Gerando seu criativo com IA...</p>
      </div>
    );
  }

  return (
    <div className="cf-phase cf-phase--creation">
      {toast && <div className="cf-toast">{toast}</div>}

      <div className="cf-mascot-row">
        <PaintBrushIcon className="cf-mascot" style={{ width: 32, height: 32 }} />
        <div className="cf-mascot-speech">Seu criativo está pronto! Personalize como quiser.</div>
      </div>

      {error && <div className="cf-alert cf-alert--error">{error} <button className="cf-link" onClick={generate}>Tentar novamente</button></div>}

      <div className="cf-creation-layout">
        {/* Preview do criativo */}
        <div className="cf-canvas-wrapper">
          {structure ? (
            <CreativeCanvasEngine key={structure._v || 'v1'} structure={structure} scale={0.3} />
          ) : (
            <div className="cf-canvas-placeholder">Nenhum criativo gerado</div>
          )}
        </div>

        {/* Painel lateral */}
        <div className="cf-sidebar-tools">

          {/* Mudar Imagem */}
          <div className="cf-tool-card">
            <h4 className="cf-tool-title"><PhotoIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 4 }} />Mudar Imagem</h4>
            <input
              className="cf-input cf-input--sm"
              placeholder="Ex: restaurante, hambúrguer..."
              value={imageQuery}
              onChange={e => setImageQuery(e.target.value)}
            />
            <button className="cf-btn cf-btn--outline cf-btn--sm" onClick={handleImageSearch} disabled={imgLoading}>
              {imgLoading ? 'Buscando...' : 'Buscar no Pexels'}
            </button>
          </div>

          {/* Ajustar Texto */}
          <div className="cf-tool-card">
            <h4 className="cf-tool-title"><PencilSquareIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 4 }} />Ajustar Texto</h4>
            {editingText ? (
              <>
                <input
                  className="cf-input cf-input--sm"
                  value={headline}
                  onChange={e => setHeadline(e.target.value)}
                  placeholder="Nova headline..."
                />
                <div className="cf-btn-row">
                  <button className="cf-btn cf-btn--primary cf-btn--sm" onClick={handleUpdateHeadline}>Aplicar</button>
                  <button className="cf-btn cf-btn--ghost cf-btn--sm" onClick={() => setEditingText(false)}>Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <p className="cf-current-text">{headline || '(sem headline)'}</p>
                <button className="cf-btn cf-btn--outline cf-btn--sm" onClick={() => setEditingText(true)}>Editar</button>
              </>
            )}
          </div>

          {/* Sugestão de Público */}
          {audience && (
            <div className="cf-tool-card cf-tool-card--audience">
              <h4 className="cf-tool-title"><UserGroupIcon style={{ width: 16, height: 16, display: 'inline', marginRight: 4 }} />Público Sugerido</h4>
              {audience.age_range && <div className="cf-audience-row"><CalendarDaysIcon style={{ width: 14, height: 14, display: 'inline', marginRight: 3 }} />Idade: <strong>{audience.age_range}</strong></div>}
              {audience.idade     && <div className="cf-audience-row"><CalendarDaysIcon style={{ width: 14, height: 14, display: 'inline', marginRight: 3 }} />Idade: <strong>{audience.idade}</strong></div>}
              {(audience.interests || audience.interesses) && (
                <div className="cf-audience-row">
                  <LightBulbIcon style={{ width: 14, height: 14, display: 'inline', marginRight: 3 }} />Interesses: <strong>{(audience.interests || audience.interesses || []).join(', ')}</strong>
                </div>
              )}
              <span className="cf-audience-saved"><CheckCircleIcon style={{ width: 14, height: 14, display: 'inline', marginRight: 3 }} />Salvo na campanha</span>
            </div>
          )}

          <button className="cf-btn cf-btn--ghost cf-btn--sm" onClick={generate}>↺ Regerar</button>
        </div>
      </div>

      <button className="cf-btn cf-btn--primary cf-btn--full" onClick={handleNext} disabled={!structure}>
        Próximo: Turbinar →
      </button>
    </div>
  );
}
